import { useState } from "react";

import BasicInput from "../../../components/inputs/basicInput/basicInput";
import BasicPanel from "../../../components/panels/basicPanel/basicPanel";
import BasicButton from "../../../components/basicButton/basicButton";
import BasicTimeSelection from "../../../components/inputs/basicTimeSelection/basicTimeSelection";
import BasicInputDropdown from "../../../components/inputs/basicInputDropdown/basicInputDropdown";
import PhoneInputField from "../../../components/inputs/phoneInput/phoneInput";

import {formatPhoneNumber, getCountryFrom, codeToCode, getCountryTo } from "./utils/phoneFormatter";
import { IconButton } from "rsuite";
import { useInvoiceContext } from '../createInvoiceContext';

import './deliveryInvoice.css';
import { isBefore, isSameDay, subDays, differenceInHours, isToday, addDays, isYesterday, format } from "date-fns";
import { useEffect } from "react";
import { getDeliveryDates, getDeliveryTime, getPVZ } from "../../../api/logistic";
import { is } from "date-fns/locale";
import { getCurrencySymbol } from "../../../utils/currencyHelper";
import { shouldApplyEAEULogic } from "../../../utils/eaeuHelper";
import { useTranslation } from "react-i18next";

const getDeliveryTypesFrom = (t) => ({
    1: t("from_door"),
    2: t("from_door"),
    3: t("from_warehouse"),
    4: t("from_warehouse"),
})

const getDeliveryTypesTo = (t) => ({
    1: t("to_door"),
    2: t("to_warehouse"),
    3: t("to_door"),
    4: t("to_warehouse"),
})

const HEADER_SVG =
<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 14.1667V7.5V2.5L14.5 6L10.5 9M7 11.0756C4.06817 11.5701 2 12.7678 2 14.1667C2 16.0076 5.58172 17.5 10 17.5C14.4183 17.5 18 16.0076 18 14.1667C18 12.7678 15.9318 11.5701 13 11.0756" stroke="url(#paint0_linear_1_3285)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <defs>
    <linearGradient id="paint0_linear_1_3285" x1="2" y1="10" x2="18" y2="10" gradientUnits="userSpaceOnUse">
    <stop stop-color="#55E5FF"/>
    <stop offset="1" stop-color="#5DFF66"/>
    </linearGradient>
    </defs>
</svg>

const PLUS_SVG =
<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.00005 1.80005L9.00005 16.2M16.2 9.00005L1.80005 9.00005" stroke="#17C653" stroke-width="2" stroke-linecap="round"/>
</svg>

const dateNeedToBeDisabled = (date, dates={all_days: true}) => {
    // Return true for all dates before today including today (if it's more that 4PM)
    const today = new Date();
    if (date < subDays(today, 1)) return true;
  
    if (isSameDay(date, today) && today.getHours() >= 16) return true;
  
    if (dates.all_days) return false;
    const localYmd = format(date, 'yyyy-MM-dd'); // date‑fns formats in your TZ
  
    return !dates.date?.includes(localYmd);
  };

const isTimeValid = (hours, deliveryTime) => {
    // Check if delivery time data is available
    if (!deliveryTime || !deliveryTime[0] || !deliveryTime[1]) {
        return false;
    }
    
    const [startHour] = deliveryTime[0];
    const [endHour] = deliveryTime[1];
    
    return hours >= startHour && hours <= endHour;
};

const getMinimumDeliveryDuration = (invoiceData) => {
    // Base minimum is 3 hours - this should NOT include break time
    // The actual delivery period should be 3 hours excluding any breaks
    return 3;
};

const hourNeedToBeDisabledFrom = (hour, date, deliveryTime, isBreak, invoiceData) => {
    // Use the selected date from invoiceData instead of the potentially incorrect date parameter
    const selectedDate = invoiceData.delivery_date_from || date;
    
    // Check if date is disabled
    if (dateNeedToBeDisabled(selectedDate)) {
        return true;
    }

    // Check if time is within delivery service hours
    if (!isTimeValid(hour, deliveryTime)) {
        return true;
    }

    // Can't select time that already passed for today (only apply this check for today)
    if (isToday(selectedDate)) {
        const currentHour = new Date().getHours();
        if (hour <= currentHour) {
            return true;
        }
    }

    if (isBreak) {
        // For break times, must be within delivery window
        if (!invoiceData.delivery_time_from || !invoiceData.delivery_time_to) {
            return true;
        }
        
        const deliveryFromHour = invoiceData.delivery_time_from.getHours();
        const deliveryToHour = invoiceData.delivery_time_to.getHours();
        
        // Break must be after delivery start and before delivery end
        if (hour <= deliveryFromHour || hour >= deliveryToHour) {
            return true;
        }
        
        return false;
    }

    // For delivery_time_from, check if we can fit minimum duration with any possible minute selection
    const minimumDurationHours = 3; // Always 3 hours, excluding breaks
    const [serviceEndHour, serviceEndMinute] = deliveryTime[1];
    
    // Calculate the latest possible start time that allows minimum duration
    // We need to check if this hour allows ANY valid minute selection that satisfies 3-hour minimum
    const serviceEndTimeDecimal = serviceEndHour + (serviceEndMinute / 60);
    
    // Check the best case scenario for this hour (selecting :00)
    const bestCaseStartTime = hour;
    const bestCaseEndTime = bestCaseStartTime + minimumDurationHours;
    
    // If even the best case (hour:00) doesn't fit, disable this hour
    if (bestCaseEndTime > serviceEndTimeDecimal) {
        return true;
    }

    return false;
};

const hourNeedToBeDisabledTo = (hour, date, deliveryTime, fromTime, isBreak, invoiceData) => {
    // Use the selected date from invoiceData instead of the potentially incorrect date parameter
    const selectedDate = invoiceData.delivery_date_from || date;
    
    // Check if date is disabled
    if (dateNeedToBeDisabled(selectedDate)) {
        return true;
    }

    // Check if time is within delivery service hours
    if (!isTimeValid(hour, deliveryTime)) {
        return true;
    }

    // Can't select time that already passed for today (only apply this check for today)
    if (isToday(selectedDate)) {
        const currentHour = new Date().getHours();
        if (hour <= currentHour) {
            return true;
        }
    }

    if (isBreak) {
        // For break end time
        if (!invoiceData.break_time_from || !invoiceData.delivery_time_to) {
            return true;
        }
        
        const breakFromHour = invoiceData.break_time_from.getHours();
        const breakFromMinute = invoiceData.break_time_from.getMinutes();
        const deliveryToHour = invoiceData.delivery_time_to.getHours();
        const deliveryToMinute = invoiceData.delivery_time_to.getMinutes();
        
        // Break end must be after break start
        if (hour <= breakFromHour) {
            return true;
        }
        
        // Break end can be exactly at delivery end time but not beyond
        if (hour > deliveryToHour) {
            return true;
        }
        
        // Check if this break end time would violate the 3-hour minimum delivery requirement
        const deliveryStart = invoiceData.delivery_time_from;
        const deliveryEnd = invoiceData.delivery_time_to;
        
        if (deliveryStart && deliveryEnd) {
            const totalDeliveryTime = differenceInHours(deliveryEnd, deliveryStart);
            
            // Calculate potential break duration with best case for this hour (using :00)
            const potentialBreakEnd = new Date(invoiceData.break_time_from);
            potentialBreakEnd.setHours(hour, 0, 0, 0);
            
            // If this hour is the delivery end hour, we can use the delivery end time
            if (hour === deliveryToHour) {
                potentialBreakEnd.setHours(deliveryToHour, deliveryToMinute, 0, 0);
            }
            
            const potentialBreakDuration = differenceInHours(potentialBreakEnd, invoiceData.break_time_from);
            const remainingDeliveryTime = totalDeliveryTime - potentialBreakDuration;
            
            // Must leave at least 3 hours for actual delivery
            if (remainingDeliveryTime < 3) {
                return true;
            }
        }
        
        return false;
    }

    // For delivery_time_to, must be after delivery_time_from + minimum duration
    if (!fromTime) {
        return true;
    }
    
    const minimumDurationHours = 3; // Always 3 hours, excluding breaks
    
    // Calculate minimum end time considering minutes
    // If start time is e.g., 15:00, minimum end time is 18:00 (exactly 3 hours is OK)
    // If start time is e.g., 15:45, minimum end time is 18:45
    // But we need to check if this hour can accommodate any valid minute selection
    
    const startTimeDecimal = fromTime.getHours() + (fromTime.getMinutes() / 60);
    const minEndTimeDecimal = startTimeDecimal + minimumDurationHours;
    
    // Check if ANY time in this hour can satisfy the minimum duration
    // The latest selectable time in this hour is hour:45 (due to 15-minute intervals)
    // But we also need to respect service end time
    const [serviceEndHour, serviceEndMinute] = deliveryTime[1];
    const serviceEndTimeDecimal = serviceEndHour + (serviceEndMinute / 60);
    
    // Find the latest valid time we can select in this hour
    let latestValidTimeInHour = hour + (45 / 60); // Assuming we can select :45
    
    // But if this hour is the service end hour, we can't go beyond service end time
    if (hour === serviceEndHour) {
        latestValidTimeInHour = Math.min(latestValidTimeInHour, serviceEndTimeDecimal);
    }
    
    // Check if this latest valid time satisfies minimum duration (use >= for inclusive)
    if (latestValidTimeInHour < minEndTimeDecimal) {
        return true;
    }

    return false;
};

const canSelectValidTimeToday = (deliveryTime, invoiceData) => {
    if (!deliveryTime || !deliveryTime[0] || !deliveryTime[1]) {
        return false;
    }
    
    const now = new Date();
    const currentHour = now.getHours();
    const [serviceStartHour] = deliveryTime[0];
    const [serviceEndHour] = deliveryTime[1];
    
    // Find earliest valid start time
    const earliestStartHour = Math.max(currentHour + 1, serviceStartHour);
    
    // Calculate minimum duration needed
    const minimumDuration = 3; // Base minimum without breaks
    const latestStartHour = serviceEndHour - minimumDuration;
    
    // Check if we have enough time today
    return earliestStartHour <= latestStartHour;
};

const shouldHideMinute = (minute, hour, deliveryTime, isBreak, invoiceData) => {
    // Only show 15-minute intervals
    if (minute % 15 !== 0) {
        return true;
    }
    
    // If no delivery time data, show all valid intervals
    if (!deliveryTime || !deliveryTime[0] || !deliveryTime[1]) {
        return false;
    }
    
    const [, serviceEndMinute] = deliveryTime[1];
    const [serviceEndHour] = deliveryTime[1];
    
    // If we're at the service end hour, only allow minutes up to service end minute
    if (hour === serviceEndHour) {
        return minute > serviceEndMinute;
    }
    
    // For delivery start time, check if this minute allows 3-hour duration within service window
    if (!isBreak && !invoiceData.delivery_time_from) {
        const minimumDurationHours = 3; // Always 3 hours, excluding breaks
        const [serviceEndHour, serviceEndMinute] = deliveryTime[1];
        const serviceEndTimeDecimal = serviceEndHour + (serviceEndMinute / 60);
        
        const currentTimeDecimal = hour + (minute / 60);
        const requiredEndTimeDecimal = currentTimeDecimal + minimumDurationHours;
        
        // If selecting this minute would require an end time beyond service hours, hide it
        if (requiredEndTimeDecimal > serviceEndTimeDecimal) {
            return true;
        }
    }
    
    // For delivery end time, check minimum duration constraint
    if (!isBreak && invoiceData.delivery_time_from) {
        const minimumDurationHours = 3; // Always 3 hours, excluding breaks
        const startTimeDecimal = invoiceData.delivery_time_from.getHours() + (invoiceData.delivery_time_from.getMinutes() / 60);
        const minEndTimeDecimal = startTimeDecimal + minimumDurationHours;
        const currentTimeDecimal = hour + (minute / 60);
        
        // Use >= for inclusive (exactly 3 hours should be allowed)
        if (currentTimeDecimal < minEndTimeDecimal) {
            return true;
        }
    }
    
    // For break times, check multiple constraints
    if (isBreak) {
        // Don't exceed delivery end time, but allow exactly delivery end time
        if (invoiceData.delivery_time_to && hour === invoiceData.delivery_time_to.getHours()) {
            return minute > invoiceData.delivery_time_to.getMinutes();
        }
        
        // For break end time, ensure we don't violate 3-hour delivery minimum
        if (invoiceData.break_time_from && invoiceData.delivery_time_from && invoiceData.delivery_time_to) {
            const deliveryStart = invoiceData.delivery_time_from;
            const deliveryEnd = invoiceData.delivery_time_to;
            const totalDeliveryTime = differenceInHours(deliveryEnd, deliveryStart);
            
            // Calculate potential break duration with this minute
            const potentialBreakEnd = new Date(invoiceData.break_time_from);
            potentialBreakEnd.setHours(hour, minute, 0, 0);
            
            // Special case: if this would be exactly the delivery end time, allow it
            if (potentialBreakEnd.getTime() === deliveryEnd.getTime()) {
                // Check if the remaining time would still be >= 3 hours
                const potentialBreakDuration = differenceInHours(potentialBreakEnd, invoiceData.break_time_from);
                const remainingDeliveryTime = totalDeliveryTime - potentialBreakDuration;
                return remainingDeliveryTime < 3;
            }
            
            const potentialBreakDuration = differenceInHours(potentialBreakEnd, invoiceData.break_time_from);
            const remainingDeliveryTime = totalDeliveryTime - potentialBreakDuration;
            
            // Must leave at least 3 hours for actual delivery
            if (remainingDeliveryTime < 3) {
                return true;
            }
        }
    }
    
    return false;
};

// Add helper function to check if any hours are available
const hasSelectableHours = (deliveryTime, fromTime, isBreak, invoiceData, date) => {
    if (!deliveryTime || !deliveryTime[0] || !deliveryTime[1]) {
        return false;
    }
    
    // Use the selected date from invoiceData, fallback to the provided date
    const selectedDate = invoiceData.delivery_date_from || date;
    
    const [serviceStartHour] = deliveryTime[0];
    const [serviceEndHour] = deliveryTime[1];
    
    // Check each hour to see if any are selectable
    for (let hour = serviceStartHour; hour <= serviceEndHour; hour++) {
        const shouldDisable = isBreak 
            ? (fromTime 
                ? hourNeedToBeDisabledTo(hour, selectedDate, deliveryTime, fromTime, isBreak, invoiceData)
                : hourNeedToBeDisabledFrom(hour, selectedDate, deliveryTime, isBreak, invoiceData))
            : (fromTime 
                ? hourNeedToBeDisabledTo(hour, selectedDate, deliveryTime, fromTime, isBreak, invoiceData)
                : hourNeedToBeDisabledFrom(hour, selectedDate, deliveryTime, isBreak, invoiceData));
        
        if (!shouldDisable) {
            return true;
        }
    }
    
    return false;
};

const DeliveryPanel = ({subject, doDelivery, doPVZ, doDeliveryTo, isTemplate}) => {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();

    const validateFio = (value) => {
        const trimmed = value.trim();
        if (!trimmed) return t("field_required");
        if (/^\d+$/.test(trimmed)) return t("name_cannot_be_only_digits");
        return null;
    };
    
    // Determine placeholder based on EAEU logic
    const isEAEU = shouldApplyEAEULogic(invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode);
    const fioPlaceholder = isEAEU ? t("fio_required") : t("fio_or_company_required");

    return (
        <>
            <div className="inputGroup">
            <BasicInput
                placeholder={fioPlaceholder}
                error={invoiceData.errors[subject]?.fio}
                value={invoiceData.delivery[subject].fio}
                onChange={(value) => {
                    setInvoiceData(oldData => ({
                        ...oldData,
                        delivery: {
                            ...oldData.delivery,
                            [subject]: {
                                ...oldData.delivery[subject],
                                fio: value
                            }
                        },
                        errors: {
                            ...oldData.errors,
                            [subject]: {
                                ...oldData.errors[subject],
                                fio: null
                            }
                        }
                    }));
                }}
                onBlur={() => {
                    setInvoiceData(oldData => {
                        const fioValue = oldData.delivery[subject].fio;
                        const error = validateFio(fioValue);
                        return {
                            ...oldData,
                            errors: {
                                ...oldData.errors,
                                [subject]: {
                                    ...oldData.errors[subject],
                                    fio: error
                                }
                            }
                        };
                    });
                }}
            />

                <BasicInput placeholder={t("contact_person_required")}
                    error={invoiceData.errors[subject] && invoiceData.errors[subject].contract_face}
                    value={invoiceData.delivery[subject].contract_face}
                    onChange={(value) => {
                        setInvoiceData(oldData => ({
                            ...oldData,
                            delivery: {
                                ...oldData.delivery,
                                [subject]: {
                                    ...oldData.delivery[subject],
                                    contract_face: value
                                }
                            }
                        }))
                    }} />

            {   (doDelivery && !isTemplate) && 
                <AddressFromPanel subject={subject} />
            }
            {   (doDelivery && isTemplate) && 
                <BasicInput placeholder={t("pickup_address_required")}
                    error={invoiceData.errors?.addressFrom}
                    value={invoiceData.addressFrom}
                    onChange={(value) => {
                        setInvoiceData(oldData => {
                            const newData = {...oldData};
                            newData.addressFrom = value;
                            return newData
                        })
                    }}
                />
            }
            {   doPVZ && 
                <PVZToPanel subject={subject} />
            }
            {   doDeliveryTo && 
                <AddressToPanel subject={subject} />
            }
            </div>
            
            <PhonesPanel subject={subject} />
        </>
    )
}

const PVZToPanel = ({subject}) => {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();
    const [ pvzs, setPvzs ] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);

        let calc_weight = 0;
        invoiceData.places.forEach((place) => {
            calc_weight += place.goods.reduce((acc, good) => acc + good.weight * good.count, 0);
        })

        let total_calculated_weight = invoiceData.places
            .reduce((acc, place) => acc + (place.height * place.width * place.length) / 5000, 0)
            .toFixed(2);

        total_calculated_weight = parseFloat(total_calculated_weight);

        // Check if tariff contains "сборный груз" to set is_ltl flag
        const isLtl = invoiceData.tariff_name && invoiceData.tariff_name.toLowerCase().includes('сборный груз');
        
        getPVZ({
            contract_number: invoiceData.contract_number,
            city_code: invoiceData.cityTo,
            weight: calc_weight > total_calculated_weight ? calc_weight : total_calculated_weight,
            pay: invoiceData.payment_on_receipt,
            trying: invoiceData?.additions?.TRYING_ON || false,
            is_ltl: isLtl
        }).then((res) => {
            setPvzs(res);
            setLoading(false);
        })
    }, []);

    useEffect(() => {
        if (pvzs?.length && invoiceData.pvzTo) {
            setInvoiceData(oldData => {
                const newData = {...oldData};
                newData.pvzToLabel = pvzs?.find(pvz => pvz.code === invoiceData.pvzTo)?.name;
                return newData
            })
        }
    }, [pvzs]);

    // Calculate weight for reminder
    const calc_weight = invoiceData.places.reduce((acc, place) => 
        acc + place.goods.reduce((acc2, good) => acc2 + good.weight * good.count, 0), 0
    );
    
    const total_calculated_weight = parseFloat(invoiceData.places
        .reduce((acc, place) => acc + (place.height * place.width * place.length) / 5000, 0)
        .toFixed(2)
    );
    
    const usedWeight = calc_weight > total_calculated_weight ? calc_weight : total_calculated_weight;
    const isUsingCalculatedWeight = total_calculated_weight > calc_weight;

    return (
        <>
        <BasicInputDropdown placeholder={t("pvz_delivery_address_required")}
            error={invoiceData.errors.pvzTo}
            loading={loading}
            data={pvzs?.map(pvz => ({label: `${pvz.code}, ${pvz.location?.city}, ${pvz.location?.address}`, value: pvz.code})) || []}
            value={invoiceData.pvzTo}
            onChange={(value) => {
                setInvoiceData(oldData => {
                    const newData = {...oldData};
                    newData.pvzTo = value;
                    newData.pvzToLabel = pvzs?.find(pvz => pvz.code === value)?.name;
                    return newData
                })
            }}
            noDataText={pvzs?.length === 0 ? t("no_suitable_pvz") : t("nothing_found")}
        />
        
        <div style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: 'rgba(23, 198, 83, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(23, 198, 83, 0.2)'
        }}>
            <div style={{
                fontSize: '12px',
                color: '#666',
                marginBottom: '8px',
                fontWeight: '500'
            }}>
                {t("pvz_selection_factors")}
            </div>
            <ul style={{
                fontSize: '12px',
                color: '#666',
                margin: '0',
                paddingLeft: '16px'
            }}>
                <li>
                    {t("goods_weight")} {isUsingCalculatedWeight ? t("volumetric_weight_used") : ''}: {usedWeight.toFixed(2)} кг
                </li>
                <li>{t("receiving_city")}: {invoiceData.cityToLabel || t("not_selected")}</li>
                {invoiceData.payment_on_receipt && <li>{t("payment_on_receipt_yes")}</li>}
                {invoiceData?.additions?.TRYING_ON && <li>{t("trying_on_needed_yes")}</li>}
            </ul>
        </div>
        </>
    )
}

const AddressToPanel = ({subject}) => {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();

    return (
        <>
        <BasicInput placeholder={t("delivery_address_required")}
            error={invoiceData.errors.addressTo}
            value={invoiceData.addressTo}
            onChange={(value) => {
                setInvoiceData(oldData => {
                    const newData = {...oldData};
                    newData.addressTo = value;
                    return newData
                })
            }}
        />
        </>
    )
}

const AddressFromPanel = ({subject}) => {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();
    const [deliveryTime, setDeliveryTime] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const [available, setAvailable] = useState([]);

    useEffect(() => {
        getDeliveryDates({
            contract_number: invoiceData.contract_number,
            from_location_code: invoiceData.cityFrom,
            day_to: addDays(new Date(), 10).toISOString().split('T')[0]
        }).then(data => {
            setAvailable(data);
        })
    }, [])

    useEffect(() => {
        // Refresh component when deliveryTime changes
    }, [refresh]);

    useEffect(() => {
        let d = new Date(invoiceData.delivery_date_from);
        d.setUTCHours(0, 0, 0, 0);

        let today = new Date();

        if (isSameDay(d, today) && today.getHours() >= 16 || isYesterday(d, today)) {
            d.setDate(d.getDate() + 1);
        }

        getDeliveryTime(
            {date: d}
        )
        .then(data => {
            setDeliveryTime(data);
        })

        setRefresh(refresh => refresh + 1);
    }, [invoiceData.delivery_date_from]);

    useEffect(() => {
        if (invoiceData.delivery_date_from) return;
        
        setInvoiceData(oldData => {
            const newData = {...oldData};

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Check if we can select a valid time period today
            if (canSelectValidTimeToday(deliveryTime, invoiceData)) {
                newData.delivery_date_from = today;
            } else {
                // If not possible today, select tomorrow
                const tomorrow = addDays(today, 1);
                newData.delivery_date_from = tomorrow;
            }

            return newData;
        });
    }, [deliveryTime]);

    useEffect(() => {
        if (!invoiceData.delivery_time_from) return;

        setInvoiceData(oldData => {
            const newData = {...oldData};
            
            // Clear delivery_time_to if it's invalid (less than 3 hours from start time)
            if (invoiceData.delivery_time_to) {
                try {
                    const timeDiff = differenceInHours(invoiceData.delivery_time_to, invoiceData.delivery_time_from);
                    if (timeDiff < 3) { // Changed from <= to < to allow exactly 3 hours
                        newData.delivery_time_to = null;
                    }
                } catch (e) {
                    // If dates are invalid, clear to time
                    newData.delivery_time_to = null;
                }
            }

            // Clear break times if delivery window becomes too small or invalid
            if (invoiceData.delivery_time_to) {
                try {
                    const deliveryDuration = differenceInHours(invoiceData.delivery_time_to, invoiceData.delivery_time_from);
                    // If total time is exactly 3 hours, no room for breaks
                    if (deliveryDuration <= 3) { 
                        newData.break_time_from = null;
                        newData.break_time_to = null;
                    }
                } catch (e) {
                    newData.break_time_from = null;
                    newData.break_time_to = null;
                }
            } else {
                // No end time set, clear breaks
                newData.break_time_from = null;
                newData.break_time_to = null;
            }

            return newData;
        });

        setRefresh(refresh => refresh + 1);
    }, [invoiceData.delivery_time_from]);

    useEffect(() => {
        if (!invoiceData.delivery_time_to) return;

        setInvoiceData(oldData => {
            const newData = {...oldData};
            
            // Clear break times if delivery window is too small
            if (invoiceData.delivery_time_from) {
                try {
                    const deliveryDuration = differenceInHours(invoiceData.delivery_time_to, invoiceData.delivery_time_from);
                    // If total time is exactly 3 hours, no room for breaks
                    if (deliveryDuration <= 3) { 
                        newData.break_time_from = null;
                        newData.break_time_to = null;
                    }
                } catch (e) {
                    newData.break_time_from = null;
                    newData.break_time_to = null;
                }
            }

            return newData;
        });
    }, [invoiceData.delivery_time_to]);

    useEffect(() => {
        if (!invoiceData.break_time_from) return;

        setInvoiceData(oldData => {
            const newData = {...oldData};

            // Clear break_time_to if it's invalid (earlier than break_time_from)
            if (invoiceData.break_time_to) {
                try {
                    if (invoiceData.break_time_from.getHours() >= invoiceData.break_time_to.getHours()) {
                        newData.break_time_to = null;
                    }
                } catch (e) {
                    newData.break_time_to = null;
                }
            }

            return newData;
        });
    }, [invoiceData.break_time_from]);

    return (
        <>
        <BasicInput placeholder={t("pickup_address_required")}
            error={invoiceData.errors?.addressFrom}
            value={invoiceData.addressFrom}
            onChange={(value) => {
                setInvoiceData(oldData => {
                    const newData = {...oldData};
                    newData.addressFrom = value;
                    return newData
                })
            }}
        />

        <BasicTimeSelection placeholder={t("pickup_date_required")}
            error={invoiceData.errors?.delivery_date_from}
            shouldDisableDate={(date) => dateNeedToBeDisabled(date, available)}
            isCalendar={true}
            value={invoiceData.delivery_date_from}
            onChange={(value) => {
                setInvoiceData(oldData => {
                    const newData = {...oldData};
                    
                    let d = new Date(value);
                    d.setHours(0, 0, 0, 0);

                    newData.delivery_date_from = d;
                    return newData
                })
            }}
        />

        <div className="inputGroupInline">
            <BasicTimeSelection placeholder={t("arrive_from_required")}
                error={invoiceData.errors?.delivery_time_from}
                format="HH:mm"
                disabled={
                    !invoiceData.delivery_date_from ||
                    !hasSelectableHours(deliveryTime, null, false, invoiceData, invoiceData.delivery_date_from)
                }
                hideMinutes={(minute, date) => shouldHideMinute(minute, date?.getHours(), deliveryTime, false, invoiceData)}
                hideHours={(hour, date) => hourNeedToBeDisabledFrom(
                    hour,
                    date,
                    deliveryTime,
                    false,
                    invoiceData
                )}
                value={invoiceData.delivery_time_from}
                onChange={(value) => {
                    setInvoiceData(oldData => {
                        const newData = {...oldData};
                        newData.delivery_time_from = value;
                        return newData
                    })
                }}
            />
            <BasicTimeSelection placeholder={t("arrive_to_required")}
                error={invoiceData.errors?.delivery_time_to}
                format="HH:mm"
                disabled={
                    !invoiceData.delivery_time_from ||
                    !hasSelectableHours(deliveryTime, invoiceData.delivery_time_from, false, invoiceData, invoiceData.delivery_date_from)
                }
                hideMinutes={(minute, date) => shouldHideMinute(minute, date?.getHours(), deliveryTime, false, invoiceData)}
                hideHours={(hour, date) => hourNeedToBeDisabledTo(
                    hour,
                    date,
                    deliveryTime,
                    invoiceData.delivery_time_from,
                    false,
                    invoiceData
                )}
                value={invoiceData.delivery_time_to}
                onChange={(value) => {
                    setInvoiceData(oldData => {
                        const newData = {...oldData};
                        newData.delivery_time_to = value;
                        return newData
                    })
                }}
            />
        </div>

        <div className="inputGroupInline">
            <BasicTimeSelection placeholder={t("break_from")}
                format="HH:mm"
                error={invoiceData.errors?.break_time_from}
                disabled={
                    !invoiceData.delivery_time_from || 
                    !invoiceData.delivery_time_to || 
                    differenceInHours(invoiceData.delivery_time_to, invoiceData.delivery_time_from) <= 3 ||
                    !hasSelectableHours(deliveryTime, null, true, invoiceData, invoiceData.delivery_date_from)
                }
                hideMinutes={(minute, date) => shouldHideMinute(minute, date?.getHours(), deliveryTime, true, invoiceData)}
                hideHours={(hour, date) => hourNeedToBeDisabledFrom(
                    hour,
                    date,
                    deliveryTime,
                    true,
                    invoiceData
                )}
                value={invoiceData.break_time_from}
                onChange={(value) => {
                    setInvoiceData(oldData => {
                        const newData = {...oldData};
                        newData.break_time_from = value;
                        return newData
                    })
                }}
            />
            <BasicTimeSelection placeholder={t("break_to")}
                format="HH:mm"
                error={invoiceData.errors?.break_time_to}
                disabled={
                    !invoiceData.break_time_from || 
                    !invoiceData.delivery_time_to ||
                    !hasSelectableHours(deliveryTime, invoiceData.break_time_from, true, invoiceData, invoiceData.delivery_date_from)
                }
                hideMinutes={(minute, date) => shouldHideMinute(minute, date?.getHours(), deliveryTime, true, invoiceData)}
                hideHours={(hour, date) => hourNeedToBeDisabledTo(
                    hour,
                    date,
                    deliveryTime,
                    invoiceData.break_time_from,
                    true,
                    invoiceData
                )}
                value={invoiceData.break_time_to}
                onChange={(value) => {
                    setInvoiceData(oldData => {
                        const newData = {...oldData};
                        newData.break_time_to = value;
                        return newData
                    })
                }}
            />
        </div>
        </>
    )
}

const PhonesPanel = ({subject}) => {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();
    // Allow only digits and: #, *, (, )
    const allowedForAdditonal = "[0-9#*()]";

    function getOnlyAllowed(value) {
        return value.replace(/[^0-9#*()]/g, '');
    }

    return (
        <>
        <div className="deliveryGroupHeader">
            <span className="paragraph-14M">{t("phones")}</span>
            { invoiceData.delivery[subject].phones.length < 10 &&
            <IconButton icon={PLUS_SVG} size='xs'
                onClick={() => {
                    setInvoiceData(oldData => ({
                        ...oldData,
                        delivery: {
                            ...oldData.delivery,
                            [subject]: {
                                ...oldData.delivery[subject],
                                phones: [...oldData.delivery[subject].phones, {number: '', additional: ''}]
                            }
                        }
                    }))
            }}/>
            }
        </div>

        {
            invoiceData.delivery[subject].phones.map((phone, index) => (
                <div className="inputGroup" style={{
                    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                    paddingBottom: "0.5rem",
                    marginBottom: "1rem"
                }} key={index}>
                    <PhoneInputField
                        disableDropdown={true}
                        defaultCountry={codeToCode(subject == "sender" ? getCountryFrom(invoiceData) : getCountryTo(invoiceData))}
                        error={invoiceData.errors[subject] && invoiceData.errors[subject].phones?.[index]?.number}
                        placeholder={t("phone_number_required")}
                        value={phone.number}
                        onChange={(value) => 
                            {   
                                setInvoiceData(oldData => ({
                                    ...oldData,
                                    delivery: {
                                        ...oldData.delivery,
                                        [subject]: {
                                            ...oldData.delivery[subject],
                                            phones: oldData.delivery[subject].phones.map((p, idx) => 
                                                idx === index ? {...p, number: value} : {...p}
                                            )
                                        }
                                    }
                                }))
                            }}
                    />
                    <BasicInput placeholder={t("additional_number")}
                        value={phone.additional}
                        onChange={(value) => {
                            setInvoiceData(oldData => ({
                                ...oldData,
                                delivery: {
                                    ...oldData.delivery,
                                    [subject]: {
                                        ...oldData.delivery[subject],
                                        phones: oldData.delivery[subject].phones.map((p, idx) => 
                                            idx === index ? {...p, additional: getOnlyAllowed(value)} : {...p}
                                        )
                                    }
                                }
                            }))
                        }} />
                    {
                        invoiceData.delivery[subject].phones.length > 1 &&
                        <BasicButton
                            style={{
                                }}
                            onClick={() => {
                                setInvoiceData(oldData => ({
                                    ...oldData,
                                    delivery: {
                                        ...oldData.delivery,
                                        [subject]: {
                                            ...oldData.delivery[subject],
                                            phones: oldData.delivery[subject].phones.filter((_, idx) => idx !== index)
                                        }
                                    }
                                }))
                            }}
                            width='33%' color="red" orient="flex-start"
                        >{t("delete")}</BasicButton>
                    }
                </div>
            ))
        }
        </>
    )
}

export default function InvoiceDelivery({isTemplate}) {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();
    const currencySymbol = getCurrencySymbol(invoiceData?.user_info);

    return (
        <div className="invoiceDelivery">
            <BasicPanel header={
                <div className="deliveryHeader">
                    {HEADER_SVG}
                    <span className="paragraph-16M">{`${t("delivery_word")} ${getDeliveryTypesFrom(t)[invoiceData.selected_mode || 1]}`}</span>
                </div>
            }>
                <DeliveryPanel isTemplate={isTemplate} subject="sender" doDelivery={[1, 2].includes(invoiceData.selected_mode)} />
                
            </BasicPanel>

            <BasicPanel header={
                <div className="deliveryHeader">
                    {HEADER_SVG}
                    <span className="paragraph-16M">{`${t("delivery_word")} ${getDeliveryTypesTo(t)[invoiceData.selected_mode || 1]}`}</span>
                </div>
            }>
                <DeliveryPanel isTemplate={isTemplate} subject="recipient" doPVZ={[2, 4].includes(invoiceData.selected_mode)} doDeliveryTo={[1, 3].includes(invoiceData.selected_mode)} />
            </BasicPanel>

            <div className="gradient_bg sum">
                <div className="line">
                    <span className="paragraph-14R">
                        {t('additional_services_cost')}
                    </span>
                    <span className="paragraph-14M">
                        {Math.ceil(invoiceData.additions_sum || 0)} {currencySymbol}
                    </span>
                </div>
                <div className="line">
                    <span className="paragraph-14R">
                        {t('delivery_sum')}
                    </span>
                    <span className="paragraph-14M">
                        {Math.ceil((invoiceData.delivery_sum || 0) * (invoiceData?.user_info?.contract_vat || 1))} {currencySymbol}
                    </span>
                </div>
                <div className="line">
                    <span className="paragraph-16R">
                        {t('total')}
                    </span>
                    <span className="paragraph-16M">
                    {Math.ceil((invoiceData.additions_sum || 0) + (invoiceData.delivery_sum || 0) * (invoiceData?.user_info?.contract_vat || 1))} {currencySymbol}
                    </span>
                </div>
            </div>
        </div>
    )
}